import { Map as ImmutableMap } from "immutable";
import { useCallback, useMemo, useRef, useState } from "react";
import { VelavuAPI, VelavuDevice } from "velavu-js-api";
import { useSubscribeCombinedDeviceUpdates } from "../../../../emitter/stream-helper";

export interface SiteDevicesCacheType {
	get(siteID: string): VelavuDevice[] | null;
	clear(): void;
	refresh(): Promise<unknown>;
}

export function useSiteDevicesCache(): SiteDevicesCacheType {
	const pendingLookups = useRef(new Set<string>()).current;
	const [siteDevices, setSiteDevices] =
		useState(ImmutableMap<string, VelavuDevice[]>());
	const lookupKey = useRef(0);

	const performLookup = useCallback(
		(siteID: string): VelavuDevice[] | null => {
			//Return if we are already looking up this site
			if (pendingLookups.has(siteID)) {
				return siteDevices.get(siteID) ?? null;
			}
			pendingLookups.add(siteID);

			const lookupKeyCopy = lookupKey.current;

			//Perform the lookup
			(async () => {
				let devices: VelavuDevice[];
				try {
					const result = await VelavuAPI.devices.getAllDevices({
						siteID,
					});
					devices = result.data;
				} catch (error) {
					console.error(error);
					return;
				}

				//Make sure the lookup key hasn't changed since we started the lookup
				if (lookupKeyCopy !== lookupKey.current) {
					return;
				}

				//Update the cache
				setSiteDevices((siteDevices) => {
					return siteDevices.set(siteID, devices);
				});
			})();
			return null;
		},
		[siteDevices, setSiteDevices],
	);

	const clear = useCallback(() => {
		//Clear the cached site devices
		setSiteDevices(ImmutableMap());

		//Clear pending lookups
		pendingLookups.clear();

		//Invalidate the lookup key
		lookupKey.current += 1;
	}, [setSiteDevices]);

	const refresh = useCallback(() => {
		//Collect the loaded site IDs
		const siteIDArray = [...siteDevices.keys()];

		//Clear loaded devices
		setSiteDevices(ImmutableMap());

		//Perform lookups for the loaded site IDs
		const lookupPromises = siteIDArray.map(async (siteID) => {
			const devicesResult = await VelavuAPI.devices.getAllDevices({
				siteID,
			});
			const devices = devicesResult.data;
			setSiteDevices((siteDevices) => {
				return siteDevices.set(siteID, devices);
			});
		});

		return Promise.all(lookupPromises);
	}, [siteDevices]);

	//Subscribe to device updates
	useSubscribeCombinedDeviceUpdates(
		useCallback(
			(update) => {
				setSiteDevices((siteDevices) => {
					const updatedDevice = update.data;
					let changed = false;

					const updatedSiteDevices = siteDevices.map(
						(devices, siteID) => {
							//Try to find the device in the list
							const deviceIndex = devices.findIndex((device) => {
								return device.id === update.id;
							});

							//Check if this device was deleted
							if (updatedDevice === null) {
								//Remove the device from the list
								if (deviceIndex !== -1) {
									changed = true;
									const updatedDevices = [...devices];
									updatedDevices.splice(deviceIndex, 1);
									return updatedDevices;
								} else {
									return devices;
								}
							}

							//Check if this device was part of this site
							if (deviceIndex !== -1) {
								//Make sure the device is still part of this site
								if (updatedDevice.site_id !== siteID) {
									//Remove the device from the list
									changed = true;
									const updatedDevices = [...devices];
									updatedDevices.splice(deviceIndex, 1);
									return updatedDevices;
								} else {
									//Update the device
									changed = true;
									const updatedDevices = [...devices];
									updatedDevices[deviceIndex] = updatedDevice;
									return updatedDevices;
								}
							} else {
								//If the device is meant for this site, add it to the list
								if (
									updatedDevice.site_id !== undefined &&
									updatedDevice.site_id === siteID
								) {
									changed = true;
									return [...devices, updatedDevice];
								} else {
									//Do nothing
									return devices;
								}
							}
						},
					);

					//Avoid updating state if nothing changed
					if (changed) {
						return updatedSiteDevices;
					} else {
						return siteDevices;
					}
				});
			},
			[setSiteDevices],
		),
	);

	return useMemo(
		(): SiteDevicesCacheType => ({
			get: performLookup,
			clear,
			refresh,
		}),
		[performLookup, clear, refresh],
	);
}
